import React from 'react';
import { Bell, BellOff } from 'lucide-react';

import type {
  MerchantCategoryCodeSummary,
  MerchantReportRiskLevel,
  MerchantReportType,
  PolicyFitStatus,
} from '@ballerine/common';
import { DIGITAL_FOOTPRINT_LITE_REPORT_TYPES } from '@ballerine/common';

import { ctw } from '@/common';
import { Card, CopyButton } from '@/components';
import { Separator } from '@/components/atoms/Separator';
import {
  CardSchemeRiskSection,
  PolicyFitBadgePremium,
  PolicyFitHeader,
  QuestionMarkTooltip,
  RiskLevelHexagon,
  RiskTypesDisplay,
} from '../../report-shared/components';
import { riskTypeMapper } from '../../report-shared/constants';

export { riskTypeMapper };

type HeaderSectionProps = {
  reportId?: string;
  homepageScreenshotUrl?: string | null;
  riskLevel?: MerchantReportRiskLevel | null;
  lineOfBusiness?: string | null;
  mcc?: MerchantCategoryCodeSummary | null;
  geographies?: string[] | null;
  riskTypes?: Record<string, { status: 'clear' | 'alert' } | null> | null;
  policyFit?: {
    status?: PolicyFitStatus;
    description?: string | null;
    name?: string | null;
    version?: number | null;
    originalPolicyFileId?: string | null;
  } | null;
  customer?: {
    displayName?: string | null;
  } | null;
  isPolicyFitEnabled?: boolean;
  isCardSchemeContentRiskEnabled?: boolean;
  cardSchemeRiskFindings?: Array<{ id: string; name?: string | null }> | null;
  isMonitoringReport: boolean;
  isAlert?: boolean;
  FeedbackComponent?: React.ComponentType<{ reportId: string }>;
  reportType: MerchantReportType;
  onOpenOriginalPolicy?: () => void;
};

export const HeaderSection: React.FC<HeaderSectionProps> = ({
  reportId,
  homepageScreenshotUrl,
  riskLevel,
  lineOfBusiness,
  mcc,
  geographies,
  riskTypes,
  policyFit,
  customer,
  isPolicyFitEnabled,
  isCardSchemeContentRiskEnabled,
  cardSchemeRiskFindings,
  isMonitoringReport,
  isAlert = false,
  FeedbackComponent,
  reportType,
  onOpenOriginalPolicy,
}) => {
  const mccText = [mcc?.primary?.mcc, mcc?.primary?.description].filter(Boolean).join(' - ');
  const isLiteReport = DIGITAL_FOOTPRINT_LITE_REPORT_TYPES.includes(
    reportType as (typeof DIGITAL_FOOTPRINT_LITE_REPORT_TYPES)[number],
  );

  return (
    <Card className="mb-6 overflow-hidden rounded-3xl bg-white">
      <div className="flex flex-col">
        {homepageScreenshotUrl && (
          <div className="relative h-80 bg-gray-100">
            <a
              href={homepageScreenshotUrl}
              target="_blank"
              rel="noreferrer"
              className="block h-full w-full cursor-pointer"
              title="Click to view full screenshot"
            >
              <img
                src={homepageScreenshotUrl}
                alt="Homepage Screenshot"
                className="h-full w-full object-cover object-top opacity-80"
                onError={e => {
                  const target = e.target as HTMLImageElement;
                  const container = target.parentElement?.parentElement;

                  if (container) {
                    container.style.display = 'none';
                  }
                }}
              />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/40" />
                <div
                  className="absolute inset-0"
                  style={{
                    backdropFilter: 'blur(14px)',
                    WebkitBackdropFilter: 'blur(14px)',
                    maskImage:
                      'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.45) 50%, rgba(0,0,0,1) 100%)',
                    WebkitMaskImage:
                      'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.45) 50%, rgba(0,0,0,1) 100%)',
                  }}
                />
              </div>
            </a>
            {isMonitoringReport && (
              <div className="absolute bottom-6 left-6 z-10 text-sm">
                <div className="z-10 flex items-center gap-2 rounded bg-white px-2 py-1 text-sm font-medium shadow-lg transition-colors hover:bg-gray-50">
                  {isAlert ? (
                    <>
                      <div className="relative">
                        <Bell className="h-4 w-4 text-neutral-900" />
                        <div className="absolute right-[1.5px] top-[1.5px] h-[6px] w-[6px] rounded-full border border-white bg-red-400" />
                      </div>
                      <span className="text-sm text-neutral-500">Detected</span>
                    </>
                  ) : (
                    <>
                      <BellOff className="h-4 w-4 text-neutral-900" />
                      <span className="text-sm text-neutral-500">No Changes</span>
                    </>
                  )}
                </div>
              </div>
            )}
            {FeedbackComponent && reportId ? (
              <div className="absolute bottom-4 right-4 z-10">
                <FeedbackComponent reportId={reportId} />
              </div>
            ) : (
              <a
                href={`mailto:support@ballerine.com?subject=${encodeURIComponent(`Feedback for Report ${reportId || ''}`)}&body=${encodeURIComponent(`Report ID: ${reportId || 'Unknown'}\n\nFeedback:\n`)}`}
                className="absolute bottom-4 right-4 z-10 rounded border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-zinc-950 transition-colors hover:bg-gray-50"
              >
                Contact Support
              </a>
            )}
          </div>
        )}

        <div className="flex gap-6 bg-white">
          <div
            className={ctw('flex', {
              'flex-1': !isLiteReport,
            })}
          >
            <div
              className={ctw(
                `flex min-w-48 flex-none flex-col items-center gap-2 py-6 text-center ${riskTypes && !isLiteReport ? 'w-1/3' : 'w-full'}`,
              )}
            >
              <h4 className="text-center text-sm font-semibold">RISK LEVEL</h4>
              <RiskLevelHexagon riskLevel={riskLevel} />
            </div>

            {!isLiteReport && riskTypes && <Separator orientation="vertical" />}

            {!isLiteReport && riskTypes && (
              <div className="w-2/3 min-w-0 py-6 pl-8">
                <div className="mb-3 flex items-center gap-1">
                  <h4 className="text-sm font-semibold text-stone-400">RISK TYPES</h4>

                  <QuestionMarkTooltip
                    text="Specific risk categories assessed including chargebacks, content analysis, reputation, transaction patterns, and legal considerations."
                    className="ml-2"
                  />
                </div>
                <RiskTypesDisplay riskTypes={riskTypes} />
              </div>
            )}
          </div>

          {!isLiteReport && (
            <div className="flex">
              <Separator orientation="vertical" />
            </div>
          )}

          <div className="flex flex-1 justify-between gap-10 py-6 pr-6">
            <div className="flex-1">
              {policyFit && isPolicyFitEnabled ? (
                <PolicyFitHeader
                  policyFit={policyFit}
                  customer={customer}
                  onOpenOriginalPolicy={onOpenOriginalPolicy}
                />
              ) : (
                <PolicyFitBadgePremium />
              )}
              {isCardSchemeContentRiskEnabled && (
                <CardSchemeRiskSection findings={cardSchemeRiskFindings ?? []} />
              )}
            </div>
            <div className="flex-1">
              <div className="mb-3 flex items-center gap-1">
                <h4 className="text-sm font-semibold text-stone-400">GENERAL INFO</h4>
                <QuestionMarkTooltip
                  text="Basic merchant information including business type, merchant category code, and geographical scope."
                  className="ml-2"
                />
              </div>
              <div className="space-y-2 text-sm">
                {lineOfBusiness && (
                  <div>
                    <span className="font-bold text-slate-900">Line Of Business</span>
                    <br />
                    <span className="text-black">{lineOfBusiness}</span>
                  </div>
                )}
                {mccText && (
                  <div>
                    <span className="font-bold text-slate-900">MCC</span>
                    <br />
                    <div className="flex items-center gap-2">
                      <span className="text-black">{mccText}</span>
                      <CopyButton textToCopy={mccText} />
                    </div>
                  </div>
                )}
                {geographies && (
                  <div>
                    <span className="font-bold text-slate-900">Top Traffic Countries</span>
                    <br />
                    <span className="text-black">{geographies.join(', ')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

HeaderSection.displayName = 'HeaderSection';
